import React from 'react';
import { LayoutDashboard, Package, DollarSign, Wrench, Settings, Truck, Tag, LogOut } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { hasPermission } from '../../utils/roleHelpers';
import apiService from '../../services/ApiService';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, perm: 'dashboard' },
  { id: 'inventory', label: 'Inventory',  icon: Package,         perm: 'inventory' },
  { id: 'financial', label: 'Financial',  icon: DollarSign,      perm: 'financial' },
  { id: 'pricing',   label: 'Pricing',    icon: Tag,             perm: 'financial' },
  { id: 'tools',     label: 'Tools',      icon: Wrench,          perm: 'tools'     },
  { id: 'shipping',  label: 'Shipping',   icon: Truck,           perm: 'shipping'  },
  { id: 'settings',  label: 'Settings',   icon: Settings,        perm: 'settings'  },
];

const ROLE_COLORS = {
  Admin:     '#84CC16',
  Manager:   '#007AFF',
  Warehouse: '#FF9500',
  Sales:     '#AF52DE',
  Finance:   '#34C759',
};

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function Sidebar({ isMobile = false }) {
  const { currentUser, currentPage, sidebarOpen, setPage, setSidebar, logout, toggleSidebar } = useApp();

  function handleNav(id) {
    setPage(id);
    if (isMobile) setSidebar(false);
  }

  async function handleLogout() {
    await apiService.logout();
    logout();
  }

  const roleColor = ROLE_COLORS[currentUser?.role] || '#84CC16';

  return (
    <div style={{
      width: isMobile ? 260 : (sidebarOpen ? 228 : 64),
      minHeight: isMobile ? '100dvh' : '100vh',
      background: '#0f172a',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      position: isMobile ? 'fixed' : 'relative',
      top: isMobile ? 0 : undefined,
      left: isMobile ? (sidebarOpen ? 0 : -280) : undefined,
      height: isMobile ? '100dvh' : undefined,
      zIndex: isMobile ? 100 : 10,
      overflowX: 'hidden',
      transition: isMobile ? 'left 0.25s cubic-bezier(0.4,0,0.2,1)' : 'width 0.2s ease',
      fontFamily: FONT,
    }}>

      {/* Logo */}
      <div style={{
        height: 56,
        display: 'flex',
        alignItems: 'center',
        padding: sidebarOpen ? '0 16px' : '0',
        justifyContent: sidebarOpen ? 'flex-start' : 'center',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
        cursor: 'pointer',
      }} onClick={isMobile ? () => setSidebar(false) : toggleSidebar}>
        <img
          src="/logo-white.png"
          alt="Premier"
          style={{
            height: sidebarOpen ? 28 : 24,
            width: 'auto',
            maxWidth: sidebarOpen ? 160 : 34,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '8px 6px', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto' }}>
        {NAV_ITEMS.filter(item => hasPermission(currentUser, item.perm)).map((item) => {
          const Icon = item.icon;
          const active = currentPage === item.id;
          return (
            <button
              key={item.id}
              onClick={() => handleNav(item.id)}
              title={!sidebarOpen ? item.label : undefined}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: sidebarOpen ? '9px 12px' : '9px 0',
                borderRadius: 10,
                border: 'none',
                background: active ? 'rgba(132,204,22,0.14)' : 'transparent',
                color: active ? '#84CC16' : 'rgba(255,255,255,0.45)',
                cursor: 'pointer',
                textAlign: 'left',
                justifyContent: sidebarOpen ? 'flex-start' : 'center',
                transition: 'background 0.12s, color 0.12s',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; } }}
            >
              <Icon size={17} style={{ flexShrink: 0 }} />
              {sidebarOpen && (
                <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                  {item.label}
                </span>
              )}
              {active && sidebarOpen && (
                <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: '#84CC16', flexShrink: 0 }} />
              )}
            </button>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '8px 6px' }}>
        {sidebarOpen && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10, marginBottom: 2 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: roleColor + '20',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: roleColor, flexShrink: 0,
            }}>
              {initials(currentUser?.name)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentUser?.name}
              </div>
              <div style={{ fontSize: 10, color: roleColor, fontWeight: 600, marginTop: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {currentUser?.role}
              </div>
            </div>
          </div>
        )}
        {!sidebarOpen && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: roleColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: roleColor }}>
              {initials(currentUser?.name)}
            </div>
          </div>
        )}

        <button
          onClick={handleLogout}
          title="Sign out"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: sidebarOpen ? '8px 12px' : '8px 0',
            borderRadius: 10,
            border: 'none',
            background: 'transparent',
            color: 'rgba(255,255,255,0.3)',
            cursor: 'pointer',
            justifyContent: sidebarOpen ? 'flex-start' : 'center',
            fontSize: 13,
            transition: 'background 0.12s, color 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,59,48,0.10)'; e.currentTarget.style.color = '#FF3B30'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}
        >
          <LogOut size={15} style={{ flexShrink: 0 }} />
          {sidebarOpen && <span style={{ fontWeight: 400 }}>Sign Out</span>}
        </button>
      </div>
    </div>
  );
}
